import {Typography} from 'components/Typography';
import React, {Dispatch, FC, useCallback, useMemo} from 'react';
import {components, GroupBase, MenuListProps, OptionProps} from 'react-select';
import {AsyncProps} from 'react-select/async';
import {Button} from 'components/Button';
import style from '../style.module.scss';
import {ISelectOption} from '..';
import {Checkbox} from 'components/Checkbox';
import {InputCaption} from 'components/InputCaption';
import Icon from 'components/Icon';

interface IProps {
    emptyMessage?: string;
    multiple?: boolean;
    onMultipleApply?: () => void;
    onMultipleClear?: () => void;
    loadOptions?: (inputValue: string, callback: (options: ISelectOption<unknown>[]) => void) => void;
    captionText?: string | React.ReactNode;
    errorText?: string;
    disabled?: boolean;
    suggestionItemClassName?: string;
    suggestionDataAutotestTarget?: string;
    suggestionDataAutotestId?: string;
    setMenuListRef?: Dispatch<React.SetStateAction<HTMLDivElement | null>>;
}

export const useSelectComponents = ({
    emptyMessage,
    multiple,
    loadOptions,
    onMultipleApply,
    onMultipleClear,
    captionText,
    errorText,
    disabled,
    suggestionItemClassName,
    suggestionDataAutotestTarget,
    suggestionDataAutotestId,
    setMenuListRef = () => {},
}: IProps) => {
    const list = useCallback(
        (props: MenuListProps & AsyncProps<unknown, boolean, GroupBase<unknown>>) => {
            const {children, isLoading: isOptionsLoading, options: listOptions} = props;

            if (loadOptions && (isOptionsLoading || !listOptions?.length)) {
                return null;
            }

            return (
                <components.MenuList className={style.listOptions} {...props} innerRef={setMenuListRef}>
                    {children}

                    {multiple && (
                        <div className={style.actions}>
                            <Button backColor={'gray'} onClick={onMultipleClear} size={'medium'}>
                                Очистить
                            </Button>
                            <Button onClick={onMultipleApply} size={'medium'}>
                                Применить
                            </Button>
                        </div>
                    )}
                </components.MenuList>
            );
        },
        [loadOptions, multiple, onMultipleApply, onMultipleClear, setMenuListRef],
    );

    const option = useCallback<FC<OptionProps>>(
        (props) => {
            const {children, isSelected, options, label} = props;
            const index = options.findIndex(({label: optionLabel}: any) => optionLabel === label);
            return (
                <components.Option {...props}>
                    <div
                        className={suggestionItemClassName}
                        data-autotest-taget-id={`${suggestionDataAutotestId}-${index + 1}`}
                        data-autotest-target={suggestionDataAutotestTarget}
                    >
                        {multiple && <Checkbox checked={isSelected} />}
                        <Typography className={style.optionText} variant={'p'}>
                            {children}
                        </Typography>
                        {!multiple && isSelected && <Icon className={style.checkMarkIcon} icon={'check'} />}
                    </div>
                </components.Option>
            );
        },
        [multiple, suggestionItemClassName, suggestionDataAutotestId, suggestionDataAutotestTarget],
    );

    const noOptionsMessage = useCallback(() => emptyMessage, [emptyMessage]);

    const caption = useMemo(
        () =>
            (captionText || errorText) && (
                <InputCaption captionText={captionText} disabled={disabled} errorText={errorText} />
            ),
        [captionText, disabled, errorText],
    );

    return {caption, list, noOptionsMessage, option};
};
